/**
 * Shared API client utilities
 *
 * Provides authentication headers and standardized fetch wrapper
 */

import {
  ApiError,
  getApiHeaders,
  handleUnauthorized,
  type StandardApiResponse,
} from '@lognai/plugin-sdk';
import { buildQueryString, buildPaginationQuery } from '@lognai/frontend-core';

export { buildQueryString, buildPaginationQuery };
export type { ListQueryParams } from '@lognai/frontend-core';
export type { PaginationParams } from '@lognai/plugin-sdk';

/**
 * Standardized fetch wrapper with error handling
 */
export const apiFetch = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', { type: 'unauthorized', status: 401 });
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: response.statusText,
    }));
    throw new ApiError(error.error || error.message || `HTTP ${response.status}`, { ...error.details, status: response.status });
  }

  // A 204 carries no body by definition, and `response.json()` on an empty one
  // throws "Unexpected end of JSON input" — which surfaced a SUCCESSFUL delete
  // as a red error toast (QA ledger PROD-001). Every plugin's client had the
  // same unguarded call.
  //
  // Deliberately narrow: only the statuses that are DEFINED to have no body are
  // special-cased. Reading every response as text first would also cover a 200
  // with an empty body, but that is speculative, and it changes the parse path
  // for every call in the product to fix a case nobody has hit.
  if (response.status === 204 || response.status === 205) return undefined as T;

  const apiResponse: StandardApiResponse<T> = await response.json();

  if (!apiResponse.success) {
    throw new ApiError(apiResponse.error || 'API request failed', { ...(apiResponse as any).details, status: response.status });
  }

  return apiResponse.data;
};
